import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import Select, {
  components,
} from "react-select";

import {
  FaDownload,
  FaRegCalendarAlt,
} from "react-icons/fa";

import api from "../axiosConfig";

import {
  useCompanySettingsData,
} from "../hooks/useCompanySettingsData";

import "../styles/OperationDetails.css";

/* ------------------------------ helpers ------------------------------ */

const absoluteFromApiBase = (
  path
) => {
  if (!path) {
    return null;
  }

  if (
    /^https?:\/\//i.test(
      path
    )
  ) {
    return path;
  }

  const base = (
    api.defaults?.baseURL ||
    ""
  ).replace(
    /\/$/,
    ""
  );

  return base
    ? `${base}${
        path.startsWith("/")
          ? ""
          : "/"
      }${path}`
    : path;
};

const toDateInput = (
  iso
) => {
  if (!iso) {
    return "";
  }

  const date =
    new Date(
      iso
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  return date
    .toISOString()
    .slice(
      0,
      10
    );
};

const niceExistingFileName = (
  value
) => {
  if (
    !value ||
    typeof value !==
      "string"
  ) {
    return "";
  }

  let name =
    value
      .split("#")[0]
      .split("?")[0];

  name =
    name
      .split("/")
      .pop() ||
    "";

  try {
    name =
      decodeURIComponent(
        name
      );
  } catch {
    // ignore
  }

  return name;
};

const isPdfUrl = (
  value
) => {
  if (
    typeof value !==
    "string"
  ) {
    return false;
  }

  const clean =
    value
      .toLowerCase()
      .trim()
      .split("#")[0]
      .split("?")[0];

  return clean.endsWith(
    ".pdf"
  );
};

const isImageUrl = (
  value
) => {
  if (
    typeof value !==
    "string"
  ) {
    return false;
  }

  const clean =
    value
      .toLowerCase()
      .trim()
      .split("#")[0]
      .split("?")[0];

  return /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/.test(
    clean
  );
};

const normalize = (
  value
) =>
  String(value || "")
    .normalize(
      "NFD"
    )
    .replace(
      /[\u0300-\u036f]/g,
      ""
    )
    .trim()
    .toLowerCase();

const NAME_TO_CODE = {
  portugal:
    "PT",

  spain:
    "ES",

  espanha:
    "ES",

  france:
    "FR",

  franca:
    "FR",

  germany:
    "DE",

  alemanha:
    "DE",

  italy:
    "IT",

  italia:
    "IT",

  netherlands:
    "NL",

  "paises baixos":
    "NL",

  belgium:
    "BE",

  belgica:
    "BE",

  luxembourg:
    "LU",

  luxemburgo:
    "LU",

  ireland:
    "IE",

  irlanda:
    "IE",

  "united kingdom":
    "GB",

  "reino unido":
    "GB",

  poland:
    "PL",

  polonia:
    "PL",

  czechia:
    "CZ",

  "czech republic":
    "CZ",

  chequia:
    "CZ",

  slovakia:
    "SK",

  eslovaquia:
    "SK",

  austria:
    "AT",

  hungry:
    "HU",

  hungary:
    "HU",

  hungria:
    "HU",

  romania:
    "RO",

  romenia:
    "RO",

  bulgaria:
    "BG",

  greece:
    "GR",

  grecia:
    "GR",

  slovenia:
    "SI",

  eslovenia:
    "SI",

  croatia:
    "HR",

  croacia:
    "HR",

  sweden:
    "SE",

  suecia:
    "SE",

  switzerland:
    "CH",

  suica:
    "CH",

  "suíça":
    "CH",

  finland:
    "FI",

  finlandia:
    "FI",

  denmark:
    "DK",

  dinamarca:
    "DK",

  estonia:
    "EE",

  latvia:
    "LV",

  letonia:
    "LV",

  lithuania:
    "LT",

  lituania:
    "LT",

  norway:
    "NO",

  noruega:
    "NO",

  iceland:
    "IS",

  islandia:
    "IS",

  "islândia":
    "IS",

  liechtenstein:
    "LI",

  andorra:
    "AD",

  monaco:
    "MC",

  "mónaco":
    "MC",

  "san marino":
    "SM",
};

const COUNTRY_OPTIONS = [
  {
    value:
      "PT",

    label:
      "Portugal",
  },

  {
    value:
      "ES",

    label:
      "Spain",
  },

  {
    value:
      "FR",

    label:
      "France",
  },

  {
    value:
      "DE",

    label:
      "Germany",
  },

  {
    value:
      "IT",

    label:
      "Italy",
  },

  {
    value:
      "NL",

    label:
      "Netherlands",
  },

  {
    value:
      "BE",

    label:
      "Belgium",
  },

  {
    value:
      "LU",

    label:
      "Luxembourg",
  },

  {
    value:
      "IE",

    label:
      "Ireland",
  },

  {
    value:
      "GB",

    label:
      "United Kingdom",
  },

  {
    value:
      "PL",

    label:
      "Poland",
  },

  {
    value:
      "CZ",

    label:
      "Czechia",
  },

  {
    value:
      "SK",

    label:
      "Slovakia",
  },

  {
    value:
      "AT",

    label:
      "Austria",
  },

  {
    value:
      "HU",

    label:
      "Hungary",
  },

  {
    value:
      "RO",

    label:
      "Romania",
  },

  {
    value:
      "BG",

    label:
      "Bulgaria",
  },

  {
    value:
      "GR",

    label:
      "Greece",
  },

  {
    value:
      "SI",

    label:
      "Slovenia",
  },

  {
    value:
      "HR",

    label:
      "Croatia",
  },

  {
    value:
      "SE",

    label:
      "Sweden",
  },

  {
    value:
      "FI",

    label:
      "Finland",
  },

  {
    value:
      "DK",

    label:
      "Denmark",
  },

  {
    value:
      "EE",

    label:
      "Estonia",
  },

  {
    value:
      "LV",

    label:
      "Latvia",
  },

  {
    value:
      "LT",

    label:
      "Lithuania",
  },

  {
    value:
      "CH",

    label:
      "Switzerland",
  },

  {
    value:
      "NO",

    label:
      "Norway",
  },

  {
    value:
      "IS",

    label:
      "Iceland",
  },

  {
    value:
      "LI",

    label:
      "Liechtenstein",
  },

  {
    value:
      "AD",

    label:
      "Andorra",
  },

  {
    value:
      "MC",

    label:
      "Monaco",
  },

  {
    value:
      "SM",

    label:
      "San Marino",
  },
];

const parseServiceAreasToCodes = (
  value
) => {
  if (
    value == null ||
    value === ""
  ) {
    return [];
  }

  if (
    typeof value ===
    "string"
  ) {
    const trimmed =
      value.trim();

    if (
      trimmed.startsWith(
        "["
      ) &&
      trimmed.endsWith(
        "]"
      )
    ) {
      try {
        const parsed =
          JSON.parse(
            trimmed
          );

        if (
          Array.isArray(
            parsed
          )
        ) {
          return parsed.map(
            (
              item
            ) =>
              String(
                item
              ).toUpperCase()
          );
        }
      } catch {
        // fallback abaixo
      }
    }
  }

  const values =
    Array.isArray(
      value
    )
      ? value
      : String(
          value
        ).split(
          /[;,]/g
        );

  return values
    .map(
      (
        item
      ) =>
        String(
          item
        ).trim()
    )
    .filter(Boolean)
    .map(
      (
        token
      ) => {
        if (
          /^[A-Za-z]{2}$/.test(
            token
          )
        ) {
          return token.toUpperCase();
        }

        const code =
          NAME_TO_CODE[
            normalize(
              token
            )
          ];

        return (
          code ||
          token.toUpperCase()
        );
      }
    );
};

const optionsFromCodes = (
  codes,
  dictionary
) => {
  const map =
    new Map(
      dictionary.map(
        (
          option
        ) => [
          option.value,
          option,
        ]
      )
    );

  return codes.map(
    (
      code
    ) =>
      map.get(
        code
      ) || {
        value:
          code,

        label:
          code,
      }
  );
};

const EMPTY_OPERATION_FORM = {
  serviceAreas: [],
  driversEmployed: "",
  communityLicenseFile: null,
  communityLicenseExpiry: "",
  cmrInsuranceFile: null,
  cmrInsuranceExpiry: "",
  transportLicenseFile: null,
  transportLicenseExpiry: "",
};

const EMPTY_OPERATION_FILES = {
  community_license: null,
  cmr_insurance: null,
  transport_license: null,
};

const operationStateFromData = (
  source
) => {
  if (!source) {
    return {
      id: null,

      form: {
        ...EMPTY_OPERATION_FORM,
      },

      existingFiles: {
        ...EMPTY_OPERATION_FILES,
      },
    };
  }

  const codes =
    parseServiceAreasToCodes(
      source.serviceAreas
    );

  const selected =
    optionsFromCodes(
      codes,
      COUNTRY_OPTIONS
    );

  return {
    id:
      source.id ??
      null,

    form: {
      serviceAreas:
        selected,

      driversEmployed:
        source.drivers_employed !=
        null
          ? String(
              source.drivers_employed
            )
          : "",

      communityLicenseFile:
        null,

      communityLicenseExpiry:
        toDateInput(
          source.community_license_expiry
        ),

      cmrInsuranceFile:
        null,

      cmrInsuranceExpiry:
        toDateInput(
          source.cmr_insurance_expiry
        ),

      transportLicenseFile:
        null,

      transportLicenseExpiry:
        toDateInput(
          source.transport_license_expiry
        ),
    },

    existingFiles: {
      community_license:
        source.community_license ||
        null,

      cmr_insurance:
        source.cmr_insurance ||
        null,

      transport_license:
        source.transport_license ||
        null,
    },
  };
};

/* ------------------------- FileField ------------------------- */

function FileField({
  label,
  existingUrl,
  value,
  onChange,
  accept = "application/pdf,image/*",
  openLabel,
  changeLabel,
  removeLabel,
}) {
  const inputRef =
    useRef(null);

  const [
    isViewerOpen,
    setIsViewerOpen,
  ] = useState(false);

  const handlePick =
    () =>
      inputRef.current?.click();

  const handleClear =
    () =>
      onChange(
        null
      );

  const hasExisting =
    Boolean(
      existingUrl
    );

  const existingHref =
    hasExisting
      ? absoluteFromApiBase(
          existingUrl
        )
      : null;

  const existingName =
    niceExistingFileName(
      existingUrl
    );

  const canPreviewInline =
    Boolean(
      existingHref
    ) &&
    (
      isPdfUrl(
        existingHref
      ) ||
      isImageUrl(
        existingHref
      )
    );

  const handleDownloadExisting =
    async () => {
      if (
        !existingHref
      ) {
        return;
      }

      const fallbackName =
        existingName ||
        "document";

      try {
        const response =
          await api.get(
            "/company-details/download-file",
            {
              responseType:
                "blob",

              params: {
                url:
                  existingHref,

                filename:
                  fallbackName,
              },
            }
          );

        const blob =
          response.data;

        const blobUrl =
          window.URL.createObjectURL(
            blob
          );

        const link =
          document.createElement(
            "a"
          );

        link.href =
          blobUrl;

        link.download =
          fallbackName;

        link.style.display =
          "none";

        document.body.appendChild(
          link
        );

        link.click();

        link.remove();

        window.setTimeout(
          () => {
            window.URL.revokeObjectURL(
              blobUrl
            );
          },
          1000
        );
      } catch (error) {
        console.error(
          "[OperationDetails] download failed:",
          error
        );

        alert(
          "Não foi possível descarregar o ficheiro automaticamente."
        );
      }
    };

  return (
    <>
      <div className="operation-details__field operation-details__filefield">
        <label className="operation-details__label">
          {label}
        </label>

        <div
          className="operation-details__filefieldRow"
          tabIndex={0}
          onKeyDown={(
            event
          ) => {
            if (
              event.key ===
              "Enter"
            ) {
              handlePick();
            }
          }}
        >
          <button
            type="button"
            className="operation-details__filechipBtn"
            onClick={
              handlePick
            }
          >
            {
              changeLabel
            }
          </button>

          {hasExisting &&
            !value && (
              <>
                <button
                  type="button"
                  className="operation-details__filechipLink--button"
                  onClick={() => {
                    if (
                      canPreviewInline
                    ) {
                      setIsViewerOpen(
                        true
                      );
                    } else if (
                      existingHref
                    ) {
                      window.open(
                        existingHref,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }
                  }}
                >
                  {
                    openLabel
                  }
                </button>

                <span
                  className="operation-details__filechipName"
                  title={
                    existingName
                  }
                >
                  {
                    existingName
                  }
                </span>
              </>
            )}

          {value && (
            <>
              <span
                className="operation-details__filechipName"
                title={
                  value.name
                }
              >
                {
                  value.name
                }
              </span>

              <button
                type="button"
                className="operation-details__filechipClear"
                onClick={
                  handleClear
                }
              >
                {
                  removeLabel
                }
              </button>
            </>
          )}
        </div>

        <input
          ref={
            inputRef
          }
          type="file"
          accept={
            accept
          }
          className="operation-details__filefieldInput"
          onChange={(
            event
          ) =>
            onChange(
              event.target.files?.[0] ||
                null
            )
          }
        />
      </div>

      {isViewerOpen &&
        existingHref && (
          <div
            className="operation-details__viewerOverlay"
            onClick={() =>
              setIsViewerOpen(
                false
              )
            }
          >
            <div
              className="operation-details__viewerModal"
              onClick={(
                event
              ) =>
                event.stopPropagation()
              }
            >
              <div className="operation-details__viewerHeader">
                <span
                  className="operation-details__viewerTitle"
                  title={
                    existingName
                  }
                >
                  {
                    existingName
                  }
                </span>

                <div className="operation-details__viewerActions">
                  <button
                    type="button"
                    className="operation-details__viewerActionBtn"
                    aria-label="Download file"
                    title="Download"
                    onClick={
                      handleDownloadExisting
                    }
                  >
                    <FaDownload />
                  </button>

                  <button
                    type="button"
                    className="operation-details__viewerClose"
                    onClick={() =>
                      setIsViewerOpen(
                        false
                      )
                    }
                    aria-label="Close viewer"
                    title="Close"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="operation-details__viewerBody">
                {isPdfUrl(
                  existingHref
                ) ? (
                  <iframe
                    title={
                      existingName ||
                      "document"
                    }
                    src={
                      existingHref
                    }
                    className="operation-details__viewerFrame"
                  />
                ) : isImageUrl(
                    existingHref
                  ) ? (
                  <img
                    src={
                      existingHref
                    }
                    alt={
                      existingName ||
                      "document"
                    }
                    className="operation-details__viewerImage"
                  />
                ) : null}
              </div>
            </div>
          </div>
        )}
    </>
  );
}

/* -------------------------------- component ------------------------------- */

export default function OperationDetails({
  embedded = false,
  onSaved,
}) {
  const { t } =
    useTranslation(
      "operation_details"
    );

  const {
    data,
    refresh,
    patchSection,
  } =
    useCompanySettingsData();

  const operations =
    data?.operations ||
    null;

  const dirtyRef =
    useRef(false);

  const [
    initialOperation,
  ] = useState(
    () =>
      operationStateFromData(
        operations
      )
  );

  const [
    companyDetailsId,
    setCompanyDetailsId,
  ] = useState(
    initialOperation.id
  );

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    form,
    setForm,
  ] = useState(
    initialOperation.form
  );

  const [
    existingFiles,
    setExistingFiles,
  ] = useState(
    initialOperation.existingFiles
  );

  useEffect(() => {
    if (
      !operations ||
      dirtyRef.current
    ) {
      return;
    }

    const next =
      operationStateFromData(
        operations
      );

    setCompanyDetailsId(
      next.id
    );

    setForm(
      next.form
    );

    setExistingFiles(
      next.existingFiles
    );

    setError(
      ""
    );
  }, [
    operations,
  ]);

  const setField = (
    key,
    value
  ) => {
    dirtyRef.current =
      true;

    setForm(
      (
        previous
      ) => ({
        ...previous,

        [key]:
          value,
      })
    );
  };

  const selectedOptions =
    form.serviceAreas ||
    [];

  const mergedOptions =
    useMemo(() => {
      const seen =
        new Set(
          COUNTRY_OPTIONS.map(
            (
              option
            ) =>
              option.value
          )
        );

      const extras =
        selectedOptions.filter(
          (
            option
          ) =>
            !seen.has(
              option.value
            )
        );

      return [
        ...COUNTRY_OPTIONS,
        ...extras,
      ];
    }, [
      selectedOptions,
    ]);

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();

      try {
        setSaving(
          true
        );

        setError(
          ""
        );

        const formData =
          new FormData();

        const serviceAreaCodes =
          (
            form.serviceAreas ||
            []
          ).map(
            (
              country
            ) =>
              country.value
          );

        formData.append(
          "serviceAreas",
          JSON.stringify(
            serviceAreaCodes
          )
        );

        if (
          form.driversEmployed !==
          ""
        ) {
          formData.append(
            "drivers_employed",
            form.driversEmployed
          );
        }

        if (
          form.communityLicenseExpiry !==
          ""
        ) {
          formData.append(
            "community_license_expiry",
            form.communityLicenseExpiry
          );
        }

        if (
          form.cmrInsuranceExpiry !==
          ""
        ) {
          formData.append(
            "cmr_insurance_expiry",
            form.cmrInsuranceExpiry
          );
        }

        if (
          form.transportLicenseExpiry !==
          ""
        ) {
          formData.append(
            "transport_license_expiry",
            form.transportLicenseExpiry
          );
        }

        if (
          form.communityLicenseFile
        ) {
          formData.append(
            "community_license",
            form.communityLicenseFile
          );
        }

        if (
          form.cmrInsuranceFile
        ) {
          formData.append(
            "cmr_insurance",
            form.cmrInsuranceFile
          );
        }

        if (
          form.transportLicenseFile
        ) {
          formData.append(
            "transport_license",
            form.transportLicenseFile
          );
        }

        let response;

        if (
          companyDetailsId
        ) {
          response =
            await api.put(
              `/company-details/${companyDetailsId}`,
              formData,
              {
                headers: {
                  "Content-Type":
                    "multipart/form-data",
                },
              }
            );
        } else {
          response =
            await api.post(
              "/company-details",
              formData,
              {
                headers: {
                  "Content-Type":
                    "multipart/form-data",
                },
              }
            );
        }

        const saved =
          response?.data ||
          {};

        const nextId =
          saved.id ??
          companyDetailsId ??
          null;

        setCompanyDetailsId(
          nextId
        );

        const savedCodes =
          saved.serviceAreas !==
          undefined
            ? parseServiceAreasToCodes(
                saved.serviceAreas
              )
            : serviceAreaCodes;

        const selected =
          optionsFromCodes(
            savedCodes,
            COUNTRY_OPTIONS
          );

        const nextExistingFiles = {
          community_license:
            saved.community_license ??
            existingFiles.community_license ??
            null,

          cmr_insurance:
            saved.cmr_insurance ??
            existingFiles.cmr_insurance ??
            null,

          transport_license:
            saved.transport_license ??
            existingFiles.transport_license ??
            null,
        };

        const nextForm = {
          ...form,

          serviceAreas:
            selected,

          communityLicenseFile:
            null,

          cmrInsuranceFile:
            null,

          transportLicenseFile:
            null,
        };

        setExistingFiles(
          nextExistingFiles
        );

        setForm(
          nextForm
        );

        dirtyRef.current =
          false;

        patchSection(
          "operations",
          {
            ...(operations ||
              {}),

            ...saved,

            id:
              nextId,

            serviceAreas:
              saved.serviceAreas ??
              JSON.stringify(
                serviceAreaCodes
              ),

            drivers_employed:
              form.driversEmployed ===
              ""
                ? null
                : Number(
                    form.driversEmployed
                  ),

            community_license:
              nextExistingFiles.community_license,

            community_license_expiry:
              form.communityLicenseExpiry ||
              null,

            cmr_insurance:
              nextExistingFiles.cmr_insurance,

            cmr_insurance_expiry:
              form.cmrInsuranceExpiry ||
              null,

            transport_license:
              nextExistingFiles.transport_license,

            transport_license_expiry:
              form.transportLicenseExpiry ||
              null,
          }
        );

        const snapshot =
          typeof onSaved ===
          "function"
            ? await onSaved()
            : await refresh();

        if (
          snapshot?.operations
        ) {
          const fresh =
            operationStateFromData(
              snapshot.operations
            );

          setCompanyDetailsId(
            fresh.id
          );

          setForm(
            fresh.form
          );

          setExistingFiles(
            fresh.existingFiles
          );
        }

        alert(
          t(
            "toasts.saved_success"
          )
        );
      } catch (submitError) {
        console.error(
          "[OperationDetails] save error:",
          submitError
        );

        const message =
          submitError?.response
            ?.data
            ?.error ||
          t(
            "toasts.saved_error"
          );

        setError(
          message
        );
      } finally {
        setSaving(
          false
        );
      }
    };

  const openDatePicker = (
    event
  ) => {
    const wrapper =
      event.currentTarget.closest(
        ".operation-details__dateWrap"
      );

    const input =
      wrapper?.querySelector(
        'input[type="date"]'
      );

    if (!input) {
      return;
    }

    if (
      typeof input.showPicker ===
      "function"
    ) {
      try {
        input.showPicker();

        return;
      } catch {
        // fallback
      }
    }

    input.focus();
    input.click();
  };

  const handleDateInputClick = (
    event
  ) => {
    if (
      typeof event.currentTarget.showPicker ===
      "function"
    ) {
      try {
        event.currentTarget.showPicker();
      } catch {
        // ignore
      }
    }
  };

  return (
    <div
      className={`operation-details ${
        embedded
          ? "operation-details--embedded"
          : ""
      }`}
    >
      <div className="operation-details__header">
        <h1 className="operation-details__title">
          {t(
            "title"
          )}
        </h1>

        <p className="operation-details__subtitle">
          {t(
            "subtitle"
          )}
        </p>
      </div>

      {error && (
        <div className="operation-details__error">
          {error}
        </div>
      )}

      <form
        className="operation-details__form"
        onSubmit={
          handleSubmit
        }
      >
        <div className="operation-details__grid">
          <div className="operation-details__field">
            <label className="operation-details__label">
              {t(
                "fields.serviceAreas.label"
              )}
            </label>

            <Select
              isMulti
              options={
                mergedOptions
              }
              value={
                selectedOptions
              }
              onChange={(
                value
              ) =>
                setField(
                  "serviceAreas",
                  value ||
                    []
                )
              }
              placeholder={t(
                "fields.serviceAreas.placeholder"
              )}
              className="operation-details-select"
              classNamePrefix="operation-details-select"
              components={{
                MultiValue: (
                  props
                ) => {
                  const MAX =
                    3;

                  const values =
                    props.getValue();

                  const index =
                    values.findIndex(
                      (
                        value
                      ) =>
                        value.value ===
                        props.data.value
                    );

                  if (
                    index >=
                    MAX
                  ) {
                    return null;
                  }

                  const hidden =
                    values.length -
                    MAX;

                  const isLastVisible =
                    index ===
                    MAX - 1;

                  return (
                    <div
                      style={{
                        display:
                          "flex",

                        alignItems:
                          "center",

                        gap:
                          6,
                      }}
                    >
                      <components.MultiValue
                        {...props}
                      />

                      {isLastVisible &&
                        hidden >
                          0 &&
                        (() => {
                          const hiddenLabels =
                            values
                              .slice(
                                MAX
                              )
                              .map(
                                (
                                  value
                                ) =>
                                  value.label
                              )
                              .join(
                                ", "
                              );

                          return (
                            <span
                              className="operation-details-select__moreCount"
                              title={
                                hiddenLabels
                              }
                              tabIndex={
                                0
                              }
                              aria-label={`More selected: ${hiddenLabels}`}
                            >
                              +
                              {
                                hidden
                              }
                            </span>
                          );
                        })()}
                    </div>
                  );
                },
              }}
            />
          </div>

          <div className="operation-details__field">
            <label className="operation-details__label">
              {t(
                "fields.driversEmployed.label"
              )}
            </label>

            <input
              type="number"
              min="0"
              value={
                form.driversEmployed
              }
              onChange={(
                event
              ) =>
                setField(
                  "driversEmployed",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.driversEmployed.placeholder"
              )}
              className="operation-details__input"
            />
          </div>

          <FileField
            label={t(
              "fields.communityLicense.label"
            )}
            existingUrl={
              existingFiles.community_license
            }
            value={
              form.communityLicenseFile
            }
            onChange={(
              file
            ) =>
              setField(
                "communityLicenseFile",
                file
              )
            }
            openLabel={t(
              "file.open"
            )}
            changeLabel={t(
              "file.change"
            )}
            removeLabel={t(
              "file.remove"
            )}
          />

          <div className="operation-details__field">
            <label className="operation-details__label">
              {t(
                "fields.communityLicenseExpiry.label"
              )}
            </label>

            <div className="operation-details__dateWrap">
              <input
                type="date"
                value={
                  form.communityLicenseExpiry
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "communityLicenseExpiry",
                    event.target.value
                  )
                }
                className="operation-details__input operation-details__input--date"
                onClick={
                  handleDateInputClick
                }
              />

              <button
                type="button"
                className="operation-details__dateBtn"
                aria-label="Open calendar"
                onClick={
                  openDatePicker
                }
              >
                <FaRegCalendarAlt className="operation-details__dateIcon" />
              </button>
            </div>
          </div>

          <FileField
            label={t(
              "fields.cmrInsurance.label"
            )}
            existingUrl={
              existingFiles.cmr_insurance
            }
            value={
              form.cmrInsuranceFile
            }
            onChange={(
              file
            ) =>
              setField(
                "cmrInsuranceFile",
                file
              )
            }
            openLabel={t(
              "file.open"
            )}
            changeLabel={t(
              "file.change"
            )}
            removeLabel={t(
              "file.remove"
            )}
          />

          <div className="operation-details__field">
            <label className="operation-details__label">
              {t(
                "fields.cmrInsuranceExpiry.label"
              )}
            </label>

            <div className="operation-details__dateWrap">
              <input
                type="date"
                value={
                  form.cmrInsuranceExpiry
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "cmrInsuranceExpiry",
                    event.target.value
                  )
                }
                className="operation-details__input operation-details__input--date"
                onClick={
                  handleDateInputClick
                }
              />

              <button
                type="button"
                className="operation-details__dateBtn"
                aria-label="Open calendar"
                onClick={
                  openDatePicker
                }
              >
                <FaRegCalendarAlt className="operation-details__dateIcon" />
              </button>
            </div>
          </div>

          <FileField
            label={t(
              "fields.transportLicense.label"
            )}
            existingUrl={
              existingFiles.transport_license
            }
            value={
              form.transportLicenseFile
            }
            onChange={(
              file
            ) =>
              setField(
                "transportLicenseFile",
                file
              )
            }
            openLabel={t(
              "file.open"
            )}
            changeLabel={t(
              "file.change"
            )}
            removeLabel={t(
              "file.remove"
            )}
          />

          <div className="operation-details__field">
            <label className="operation-details__label">
              {t(
                "fields.transportLicenseExpiry.label"
              )}
            </label>

            <div className="operation-details__dateWrap">
              <input
                type="date"
                value={
                  form.transportLicenseExpiry
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "transportLicenseExpiry",
                    event.target.value
                  )
                }
                className="operation-details__input operation-details__input--date"
                onClick={
                  handleDateInputClick
                }
              />

              <button
                type="button"
                className="operation-details__dateBtn"
                aria-label="Open calendar"
                onClick={
                  openDatePicker
                }
              >
                <FaRegCalendarAlt className="operation-details__dateIcon" />
              </button>
            </div>
          </div>
        </div>

        <div className="operation-details__actions">
          <button
            type="button"
            className="operation-details__btn operation-details__btn--ghost"
            onClick={() =>
              window.history.back()
            }
            disabled={
              saving
            }
          >
            {t(
              "actions.cancel"
            )}
          </button>

          <button
            type="submit"
            className="operation-details__btn operation-details__btn--primary"
            disabled={
              saving
            }
          >
            {saving
              ? t(
                  "actions.saving"
                )
              : t(
                  "actions.save"
                )}
          </button>
        </div>
      </form>
    </div>
  );
}